import demoExercisesSeed from './demoExercisesSeed.js';

const AGE_GROUPS = ['U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18'];

const FIRST_NAMES = [
  'Adam', 'Bartosz', 'Cezary', 'Dawid', 'Eryk',
  'Filip', 'Grzegorz', 'Hubert', 'Igor', 'Jakub',
  'Kacper', 'Leon', 'Mateusz', 'Michał', 'Nikodem',
  'Oskar', 'Patryk', 'Rafał', 'Szymon', 'Tymon',
  'Wiktor', 'Jan', 'Natan', 'Piotr', 'Radosław',
  'Sebastian', 'Tomasz', 'Urszula', 'Wojciech', 'Zenon',
];

const LAST_NAMES = [
  'Kowalski', 'Nowak', 'Wiśniewski', 'Wójcik', 'Kowalczyk',
  'Kamiński', 'Lewandowski', 'Zieliński', 'Szymański', 'Woźniak',
];

const SHOT_PATTERNS = {
  progress: {
    made3: [6, 8, 10, 12],
    made2: [9, 11, 12, 14],
    madeFt: [11, 13, 15, 17],
  },
  stable: {
    made3: [8, 8, 9, 8],
    made2: [11, 11, 11, 10],
    madeFt: [14, 14, 13, 14],
  },
  regression: {
    made3: [13, 12, 10, 8],
    made2: [15, 14, 12, 11],
    madeFt: [17, 16, 15, 13],
  },
};

export const DEMO_STORAGE_KEY = 'pkk-training-demo-state-v2';
export const DEMO_CURRENT_USER_ID = 'demo-admin';

const iso = (daysAgo = 0, hoursAgo = 0) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(date.getHours() - hoursAgo);
  return date.toISOString();
};

const getName = (groupIndex, playerIndex) => {
  const firstName = FIRST_NAMES[(groupIndex * 10 + playerIndex) % FIRST_NAMES.length];
  const lastName = LAST_NAMES[(groupIndex + playerIndex) % LAST_NAMES.length];
  return `${firstName} - test${AGE_GROUPS[groupIndex]} ${lastName} test ${String(playerIndex + 1).padStart(2, '0')}`;
};

const splitName = (fullName) => {
  const parts = fullName.split(' ');
  const firstName = parts[0] || 'Zawodnik';
  const lastName = parts.slice(1).join(' ') || 'Demo';
  return { firstName, lastName };
};

const createPlayerProfile = ({ uid, name, groupIndex, playerIndex }) => {
  const baseHeight = 150 + groupIndex * 4 + (playerIndex % 3) * 3;
  const baseWeight = 42 + groupIndex * 2 + (playerIndex % 4) * 2;
  const { firstName, lastName } = splitName(name.replace(/ - test.*/, '').trim());
  const dietaryRestrictions = playerIndex % 3 === 0 ? ['lactose_free'] : [];
  const birthDateMonth = new Date(2010 + groupIndex - Math.floor(playerIndex / 2), (playerIndex * 2) % 12, 1).toISOString().slice(0, 7);

  return {
    uid,
    name,
    firstName,
    lastName,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase().replace(/\s+/g, '.')}@pkk-demo.local`,
    birthMonthYear: birthDateMonth,
    dietaryRestrictions,
    height: baseHeight,
    weight: baseWeight,
    periodicExaminationsProvided: playerIndex % 2 === 0,
  };
};

const createShotStats = ({ uid, name, groupId, groupName, groupIndex, patternName }) => {
  const pattern = SHOT_PATTERNS[patternName];

  return Array.from({ length: 4 }, (_, sessionIndex) => ({
    sessionName: `Test rzutowy ${sessionIndex + 1}`,
    groupId,
    groupName,
    playerId: uid,
    playerName: name,
    attempts3: 20,
    made3: pattern.made3[sessionIndex],
    attempts2: 20,
    made2: pattern.made2[sessionIndex],
    attemptsFt: 20,
    madeFt: pattern.madeFt[sessionIndex],
    notes:
      patternName === 'progress'
        ? 'Widoczny postęp techniczny i skuteczności.'
        : patternName === 'stable'
          ? 'Wynik utrzymany bez wyraźnej zmiany.'
          : 'Spadek skuteczności w porównaniu do poprzedniej sesji.',
    trend: patternName,
    createdAt: iso(21 - sessionIndex * 5, groupIndex),
  }));
};

const createExercises = () => {
  if (Array.isArray(demoExercisesSeed) && demoExercisesSeed.length > 0) {
    return demoExercisesSeed.map((exercise) => ({
      createdById: DEMO_CURRENT_USER_ID,
      isPublic: true,
      rating: 0,
      usageCount: 0,
      ...exercise,
    }));
  }

  return [
    {
      id: 'ex-warmup',
      name: 'Rozgrzewka koordynacyjna',
      description: 'Lekka rozgrzewka i aktywacja całego ciała.',
      category: 'warmup',
      difficulty: 'easy',
      duration: 12,
      repetitions: 1,
      youtubeUrl: '',
      createdById: DEMO_CURRENT_USER_ID,
      createdAt: iso(34),
      isPublic: true,
      rating: 4.8,
      usageCount: 18,
    },
    {
      id: 'ex-shooting-mid',
      name: 'Rzuty z półdystansu',
      description: 'Seria rzutów z półdystansu po koźle.',
      category: 'shooting',
      difficulty: 'medium',
      duration: 18,
      repetitions: 40,
      youtubeUrl: '',
      createdById: DEMO_CURRENT_USER_ID,
      createdAt: iso(33),
      isPublic: true,
      rating: 4.9,
      usageCount: 24,
    },
    {
      id: 'ex-shooting-three',
      name: 'Rzuty z dystansu',
      description: 'Praca nad stabilnym mechanizmem rzutu z dystansu.',
      category: 'shooting',
      difficulty: 'hard',
      duration: 15,
      repetitions: 30,
      youtubeUrl: '',
      createdById: DEMO_CURRENT_USER_ID,
      createdAt: iso(32),
      isPublic: true,
      rating: 4.7,
      usageCount: 16,
    },
    {
      id: 'ex-free-throws',
      name: 'Rzuty wolne pod zmęczeniem',
      description: 'Seria rzutów wolnych po intensywnym biegu.',
      category: 'shooting',
      difficulty: 'medium',
      duration: 10,
      repetitions: 20,
      youtubeUrl: '',
      createdById: DEMO_CURRENT_USER_ID,
      createdAt: iso(31),
      isPublic: true,
      rating: 4.6,
      usageCount: 20,
    },
  ];
};

const createChallenges = () => [
  {
    id: 'ch-accuracy',
    name: 'Tygodniowa skuteczność',
    description: 'Najlepsza skuteczność rzutowa w tygodniu.',
    type: 'individual',
    metric: 'accuracy',
    goal: 70,
    startDate: iso(14),
    endDate: iso(-14),
    createdBy: DEMO_CURRENT_USER_ID,
    participants: [],
    isActive: true,
    createdAt: iso(14),
  },
  {
    id: 'ch-volume',
    name: 'Objętość treningowa',
    description: 'Najwięcej ukończonych treningów w miesiącu.',
    type: 'team',
    metric: 'workouts',
    goal: 12,
    startDate: iso(20),
    endDate: iso(-10),
    createdBy: DEMO_CURRENT_USER_ID,
    participants: [],
    isActive: true,
    createdAt: iso(20),
  },
];

const createUsersAndGroups = () => {
  const users = [];
  const groups = [];
  const performanceLogs = [];
  const workouts = [];
  const schedules = [];

  const adminShotStats = createShotStats({
    uid: DEMO_CURRENT_USER_ID,
    name: 'Konto Demo',
    groupId: 'u18',
    groupName: 'Drużyna U18',
    groupIndex: 7,
    patternName: 'progress',
  });

  const adminProfile = createPlayerProfile({ uid: DEMO_CURRENT_USER_ID, name: 'Konto Demo', groupIndex: 7, playerIndex: 0 });

  users.push({
    id: DEMO_CURRENT_USER_ID,
    uid: DEMO_CURRENT_USER_ID,
    name: 'Konto Demo',
    displayName: 'Konto Demo',
    team: 'PKK 99',
    roles: ['admin', 'trainer', 'player'],
    role: 'admin',
    activeRole: 'trainer',
    voiceEnabled: true,
    voiceVolume: 1,
    voiceRate: 1,
    ranking: '1',
    performanceTrend: 'progress',
    shotStats: adminShotStats,
    profile: adminProfile,
    firstName: adminProfile.firstName,
    lastName: adminProfile.lastName,
    email: adminProfile.email,
    birthMonthYear: adminProfile.birthMonthYear,
    dietaryRestrictions: adminProfile.dietaryRestrictions,
    height: adminProfile.height,
    weight: adminProfile.weight,
    periodicExaminationsProvided: adminProfile.periodicExaminationsProvided,
    notes: [
      {
        text: 'Konto demo do uruchomienia panelu bez Firestore.',
        authorName: 'System',
        createdAt: iso(2),
      },
    ],
    notifications: [
      {
        title: 'Tryb demo',
        message: 'Aplikacja działa na lokalnych danych testowych.',
        createdAt: iso(1),
      },
    ],
    createdAt: iso(45),
    updatedAt: iso(1),
  });

  adminShotStats.forEach((entry, index) => {
    performanceLogs.push({
      id: `demo-admin-log-${index + 1}`,
      ...entry,
      source: 'demo',
    });
  });

  AGE_GROUPS.forEach((ageGroup, groupIndex) => {
    const groupId = ageGroup.toLowerCase();
    const groupName = `Drużyna ${ageGroup}`;
    const members = Array.from({ length: 10 }, (_, playerIndex) => {
      const uid = `demo-${groupId}-${String(playerIndex + 1).padStart(2, '0')}`;
      const patternName = playerIndex < 4 ? 'progress' : playerIndex < 7 ? 'stable' : 'regression';
      const name = getName(groupIndex, playerIndex);
      const shotStats = createShotStats({
        uid,
        name,
        groupId,
        groupName,
        groupIndex,
        patternName,
      });
      const totalAttempts = shotStats.reduce((sum, entry) => sum + entry.attempts3 + entry.attempts2 + entry.attemptsFt, 0);
      const totalMade = shotStats.reduce((sum, entry) => sum + entry.made3 + entry.made2 + entry.madeFt, 0);

      const profile = createPlayerProfile({ uid, name, groupIndex, playerIndex });

      users.push({
        id: uid,
        uid,
        name,
        displayName: name,
        team: `PKK ${ageGroup}`,
        ageCategory: ageGroup,
        roles: ['player'],
        role: 'player',
        activeRole: 'player',
        voiceEnabled: true,
        voiceVolume: 1,
        voiceRate: 1,
        ranking: `${groupIndex + 1}.${playerIndex + 1}`,
        performanceTrend: patternName,
        shotStats,
        profile,
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        birthMonthYear: profile.birthMonthYear,
        dietaryRestrictions: profile.dietaryRestrictions,
        height: profile.height,
        weight: profile.weight,
        periodicExaminationsProvided: profile.periodicExaminationsProvided,
        notes: [
          {
            text:
              patternName === 'progress'
                ? 'Zawodnik poprawia skuteczność z tygodnia na tydzień.'
                : patternName === 'stable'
                  ? 'Stabilny poziom bez wyraźnych skoków.'
                  : 'Wynik spada po dobrym początku.',
            authorName: 'System demo',
            createdAt: iso(6, groupIndex),
          },
        ],
        notifications: [],
        createdAt: iso(40 - groupIndex),
        updatedAt: iso(1),
        statsSummary: {
          sessions: shotStats.length,
          totalMade,
          totalAttempts,
          accuracy: Math.round((totalMade / totalAttempts) * 1000) / 10,
        },
      });

      shotStats.forEach((entry, sessionIndex) => {
        performanceLogs.push({
          id: `demo-${uid}-log-${sessionIndex + 1}`,
          ...entry,
          source: 'demo',
        });
      });

      return uid;
    });

    groups.push({
      id: groupId,
      name: groupName,
      description: `Testowa grupa wiekowa ${ageGroup}.`,
      trainerId: DEMO_CURRENT_USER_ID,
      trainerName: 'Konto Demo',
      members,
      createdAt: iso(45),
      updatedAt: iso(1),
      ageCategory: ageGroup,
      systemSeeded: true,
    });

    Array.from({ length: 3 }, (_, workoutIndex) => {
      const createdAt = iso(14 - (groupIndex * 2) - workoutIndex, 2 + workoutIndex);
      const exercises = [
        { name: 'Rozgrzewka koordynacyjna', duration: 12, repetitions: 1 },
        { name: 'Rzuty z półdystansu', duration: 18, repetitions: 40 },
        { name: 'Rzuty z dystansu', duration: 15, repetitions: 30 },
        { name: 'Rzuty wolne pod zmęczeniem', duration: 10, repetitions: 20 },
      ];
      workouts.push({
        id: `demo-${groupId}-workout-${String(workoutIndex + 1).padStart(2, '0')}`,
        name: `${groupName} - trening ${workoutIndex + 1}`,
        description: `Testowy trening ${workoutIndex + 1} dla grupy ${ageGroup}.`,
        groupId,
        groupName,
        userId: DEMO_CURRENT_USER_ID,
        createdBy: DEMO_CURRENT_USER_ID,
        createdAt,
        updatedAt: createdAt,
        visibilityScope: 'all',
        isPublic: true,
        sharedWith: members,
        exercises,
        totalDuration: exercises.reduce((sum, exercise) => sum + exercise.duration, 0),
        totalExercises: exercises.length,
      });
    });

    Array.from({ length: 2 }, (_, scheduleIndex) => {
      const workout = workouts[workouts.length - 1 - scheduleIndex];
      schedules.push({
        id: `demo-${groupId}-schedule-${String(scheduleIndex + 1).padStart(2, '0')}`,
        workoutId: workout.id,
        date: iso(7 - groupIndex - scheduleIndex).slice(0, 10),
        time: `${18 + scheduleIndex}:00`,
        team: ageGroup,
        scope: 'group',
        groupId,
        createdById: DEMO_CURRENT_USER_ID,
        notes: `Plan treningowy dla grupy ${ageGroup}.`,
        createdAt: iso(7 - groupIndex - scheduleIndex),
      });
    });
  });

  return { users, groups, workouts, schedules, exercises: createExercises(), challenges: createChallenges(), performanceLogs };
};

export const buildDemoDataset = () => {
  const { users, groups, workouts, schedules, exercises, challenges, performanceLogs } = createUsersAndGroups();

  return {
    meta: {
      currentUserId: DEMO_CURRENT_USER_ID,
    },
    users,
    trainingGroups: groups,
    workouts,
    schedules,
    exercises,
    challenges,
    performanceLogs,
    playerNotes: [],
    userStats: [],
  };
};

export const buildDemoCurrentUser = () => ({
  uid: DEMO_CURRENT_USER_ID,
  email: 'demo@pkk-training.local',
  displayName: 'Konto Demo',
  photoURL: '',
  roles: ['admin', 'trainer', 'player'],
  role: 'admin',
  activeRole: 'admin',
});

export const getDemoUserById = (dataset, userId) => dataset.users.find((user) => user.uid === userId || user.id === userId) || null;
